import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import React, { FC } from 'react';
import { SubmitButton } from '@app/components/SubmitButton';
import { useDepositLiquidity } from '@app/hooks/router-intent/useDepositLiquidity';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { useRouterIntentState } from '@app/state/routerIntentStore';
import { useChainId, useSwitchChain } from '@thirdweb-dev/react';
import { TokensPair } from '@app/components/TokensPair';
import { formatDuration } from '@app/helpers/time';
import { useWalletBalance } from '@app/hooks/thirdweb/useWalletBalance';
import {
  GetProtocolQuotaResponse,
  ResponseError
} from '@app/hooks/router-intent/types';
import { TransactionReceipt } from 'node_modules/thirdweb/dist/types/transaction/types';

function getSubmitLabel(isLoading: boolean, isNetworkMismatch: boolean) {
  if (isNetworkMismatch) {
    return 'Switch chain';
  }

  return isLoading ? 'Processing...' : 'Deposit liquidity';
}

interface Props {
  details: GetProtocolQuotaResponse | null | undefined;
  errors: ResponseError[] | undefined;
  isLoading: boolean;
  isAvailable: boolean;
  depositLiquidity: () => Promise<TransactionReceipt | undefined>;
}

export const SubmitRouting: FC<Props> = ({
  details,
  errors,
  isAvailable,
  isLoading,
  depositLiquidity
}) => {
  const switchChain = useSwitchChain();
  const chainId = useChainId();
  const { sourceNetwork, sourceToken, sourceAmount } = useRouterIntentState();

  const { data: balance } = useWalletBalance(sourceNetwork, sourceToken);

  const isBalanceExceeded = Boolean(
    balance && sourceAmount && +sourceAmount > +balance.displayValue
  );

  return (
    <Box w="100%">
      {details?.PayLoad && (
        <VStack
          width="100%"
          gap={4}
          mt={4}
          border="1px solid"
          borderColor="neutral.700"
          p="12px"
          borderRadius="12px"
        >
          <HStack
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text fontSize="14px" color="neutral.400">
              ETA
            </Text>
            <Text fontSize="14px" color="neutral.100" fontWeight={500}>
              {formatDuration(details.PayLoad.estimatedTime)}
            </Text>
          </HStack>
          <HStack
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text fontSize="14px" color="neutral.400">
              Slippage
            </Text>
            <Text fontSize="14px" color="neutral.100" fontWeight={500}>
              {details.PayLoad.slippageTolerance}%
            </Text>
          </HStack>
        </VStack>
      )}

      {errors?.map((error, i) => {
        return (
          <Alert
            key={i}
            status="error"
            display="flex"
            border="1px solid"
            borderColor={'danger.800'}
            mt="16px"
            borderRadius="10px"
            py="10px"
            px="12px"
          >
            {status === 'error' ? (
              <AlertIcon w="16px" h="16px" mr="8px" color="orange" />
            ) : (
              <AlertIcon w="16px" h="16px" mr="8px" color="whiteOpacity.50" />
            )}
            <AlertDescription color="danger.200">
              {error.Message.title}: {error.Message.message}
            </AlertDescription>
          </Alert>
        );
      })}
      <SubmitButton
        isLoading={isLoading}
        label={getSubmitLabel(isLoading, chainId !== sourceNetwork?.chainId)}
        disabled={isLoading || !isAvailable || isBalanceExceeded}
        onClick={async () => {
          if (!isAvailable || !sourceNetwork) {
            return;
          }

          if (chainId !== sourceNetwork.chainId) {
            await switchChain(sourceNetwork.chainId);
          } else {
            await depositLiquidity();
          }
        }}
      />
    </Box>
  );
};
